import React, { useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const shrink = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, 600 / Math.max(img.width, img.height));
    const c = document.createElement('canvas');
    c.width = Math.round(img.width * scale);
    c.height = Math.round(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    URL.revokeObjectURL(img.src);
    resolve(c.toDataURL('image/jpeg', 0.85));
  };
  img.onerror = reject;
  img.src = URL.createObjectURL(file);
});

export const PhotoPicker = ({ onChange, hasExisting }) => {
  const { t } = useLanguage();
  const input = useRef(null);
  const [preview, setPreview] = useState(null);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await shrink(file);
    setPreview(url);
    onChange(url);
  };
  const clear = () => { setPreview(null); onChange(null); if (input.current) input.current.value = ''; };

  return (
    <div className="flex items-center gap-4 rounded-xl border border-dashed p-3" data-testid="utt-photo-picker">
      <div className="flex h-24 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
        {preview ? <img src={preview} alt="Passport" className="h-full w-full object-cover" data-testid="utt-photo-preview" />
          : <Camera className="h-6 w-6 text-muted-foreground" />}
      </div>
      <div className="space-y-1 text-xs text-muted-foreground">
        <p className="font-medium text-foreground">{t('Passport photo (optional)', 'Picha ya pasipoti (si lazima)')}</p>
        <p>{hasExisting && !preview ? t('A photo is already on file. Add a new one to replace it.', 'Picha tayari ipo. Weka mpya kuibadilisha.')
          : t('Take a selfie or choose a photo: face the camera, plain background.', 'Piga selfie au chagua picha: angalia kamera, mandhari safi.')}</p>
        <div className="flex gap-3">
          <button type="button" onClick={() => input.current?.click()} className="font-medium text-primary" data-testid="utt-photo-add">
            {preview ? t('Change photo', 'Badilisha picha') : t('Add photo', 'Weka picha')}
          </button>
          {preview && <button type="button" onClick={clear} className="flex items-center gap-1 text-red-600" data-testid="utt-photo-remove">
            <X className="h-3 w-3" />{t('Remove', 'Ondoa')}</button>}
        </div>
      </div>
      <input ref={input} type="file" accept="image/jpeg,image/png" className="hidden" onChange={pick} data-testid="utt-photo-input" />
    </div>
  );
};
